import { put, call, all, select, takeLatest, takeEvery } from 'redux-saga/effects';
import services from '../../services';

import {
  GET_COMMENTS,
  GET_COMMENTS_SUCCESS,
  GET_COMMENTS_ERROR,
} from '../types/comment/get-comments';

import {
  NEW_COMMENT,
  NEW_COMMENT_SUCCESS,
  NEW_COMMENT_ERROR,
} from '../types/comment/new-comment';

import {
  DELETE_COMMENT,
  DELETE_COMMENT_SUCCESS,
  DELETE_COMMENT_ERROR,
} from '../types/comment/delete-comment';

import { UPDATE_CURRENT_POST } from '../types/post/get-post';

import { GetCommentsOutDto, CreateCommentOutDto, DeleteCommentOutDto } from '../../types/comment.dto';

const getCurrentPost = ({ postStore }) => postStore.post.data;
const getCurrentComments = ({ commentStore }) => commentStore.comments.data;

function* fetchComments({ payload }: { payload: GetCommentsOutDto & { append?: boolean } }): any {
  try {
    const { response, error } = yield call(services.comment.getComments, payload);

    if (error) {
      yield put({ type: GET_COMMENTS_ERROR, payload: error });
    } else {
      yield put({ type: GET_COMMENTS_SUCCESS, payload: response, append: !!payload.append });
    }
  } catch (error) {
    yield put({ type: GET_COMMENTS_ERROR, payload: 'Error al cargar los comentarios.' });
  }
}

function* createComment({ payload }: { payload: CreateCommentOutDto }): any {
  try {
    const { response, error } = yield call(services.comment.createComment, payload);

    if (error) {
      yield put({ type: NEW_COMMENT_ERROR, payload: error });
    } else {
      const post = yield select(getCurrentPost);
      const commentsData = yield select(getCurrentComments);

      const newComment = response;
      let updatedItems = commentsData?.data ? [...commentsData.data] : [];

      if (newComment.parent_id) {
        // It's a reply — add it inside the parent comment's replies array
        updatedItems = updatedItems.map((c) => {
          if (c.id === newComment.parent_id) {
            return {
              ...c,
              replies: [...(c.replies || []), newComment],
              reply_count: (c.reply_count || 0) + 1,
            };
          }
          return c;
        });
      } else {
        // Top-level comment — prepend to list
        updatedItems = [newComment, ...updatedItems];
      }

      const actions: any[] = [
        put({ type: NEW_COMMENT_SUCCESS, payload: newComment }),
        put({
          type: GET_COMMENTS_SUCCESS,
          payload: {
            data: updatedItems,
            pagination: commentsData?.pagination
              ? {
                  ...commentsData.pagination,
                  total: commentsData.pagination.total + 1,
                }
              : { total: 1, lastPage: 1, perPage: 20, currentPage: 1 },
          },
          append: false,
        }),
      ];

      // Optimistically increment comments_count on the post
      if (post) {
        actions.push(
          put({
            type: UPDATE_CURRENT_POST,
            payload: { ...post, comments_count: (post.comments_count || 0) + 1 },
          })
        );
      }

      yield all(actions);
    }
  } catch (error) {
    yield put({ type: NEW_COMMENT_ERROR, payload: 'Error al publicar el comentario.' });
  }
}

function* removeComment({ payload }: { payload: DeleteCommentOutDto }): any {
  try {
    const { response, error } = yield call(services.comment.deleteComment, payload);

    if (error) {
      yield put({ type: DELETE_COMMENT_ERROR, payload: error });
    } else {
      const post = yield select(getCurrentPost);
      const commentsData = yield select(getCurrentComments);

      // Soft-delete: mark comment as deleted in local state (is_deleted: true, body: null, subscriber: null)
      const updatedItems = (commentsData?.data || []).map((c) => {
        if (c.id === payload.id) {
          // Top-level comment deleted → cascade to all its replies as well
          return {
            ...c,
            is_deleted: true,
            body: null,
            subscriber: null,
            replies: (c.replies || []).map((r) => ({
              ...r,
              is_deleted: true,
              body: null,
              subscriber: null,
            })),
          };
        }
        // Reply deleted → only mark that reply; parent and siblings unaffected
        if (c.replies) {
          return {
            ...c,
            replies: c.replies.map((r) =>
              r.id === payload.id
                ? { ...r, is_deleted: true, body: null, subscriber: null }
                : r
            ),
          };
        }
        return c;
      });

      const actions: any[] = [
        put({ type: DELETE_COMMENT_SUCCESS, payload: null }),
        put({
          type: GET_COMMENTS_SUCCESS,
          payload: {
            data: updatedItems,
            pagination: commentsData?.pagination
              ? {
                  ...commentsData.pagination,
                  total: Math.max(0, commentsData.pagination.total - 1),
                }
              : commentsData?.pagination,
          },
          append: false,
        }),
      ];

      // Optimistically decrement comments_count on the post
      if (post) {
        actions.push(
          put({
            type: UPDATE_CURRENT_POST,
            payload: {
              ...post,
              comments_count: Math.max(0, (post.comments_count || 0) - 1),
            },
          })
        );
      }

      yield all(actions);
    }
  } catch (error) {
    yield put({ type: DELETE_COMMENT_ERROR, payload: 'Error al eliminar el comentario.' });
  }
}

export default function* commentSaga() {
  yield takeLatest<any>(GET_COMMENTS, fetchComments);
  yield takeEvery<any>(NEW_COMMENT, createComment);
  yield takeEvery<any>(DELETE_COMMENT, removeComment);
}
